import axios from "axios";
import { getSlug, setHrefWithSlugHook } from "../config/routes/guards/slug";
import {
  CPANEL_TOKEN_KEY,
  clearCpanelToken,
  clearCentralUser,
  logoutCpanel,
} from "../helpers/cpanelAuth";
import {
  clearHospitalToken,
  getHospitalToken,
  isHospitalLoginRoute,
} from "../helpers/hospitalAuth";
import { MySwal } from "./swal";

const isCpanelPath = () => {
  const path = window.location.pathname;
  return path === "/" || path.startsWith("/cpanel");
};

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

instance.interceptors.request.use(
  async config => {
    if (isCpanelPath()) {
      const cpanelToken = localStorage.getItem(CPANEL_TOKEN_KEY);
      if (cpanelToken) {
        config.headers.Authorization = `Bearer ${cpanelToken}`;
      }
      return config;
    }

    const slug = getSlug();

    config.headers.slug = slug;

    const hospitalToken = getHospitalToken();
    if (hospitalToken) {
      config.headers.Authorization = `Bearer ${hospitalToken}`;
    }

    if (slug && !(config.url && config.url.endsWith("/validate"))) {
      try {
        const validateResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/validate`,
          {
            headers: {
              'slug': slug,
            },
          }
        );
      } catch (error) {
        if (error.response && error.response.status === 422) {
          window.location.href = "/";
        }
        return Promise.reject(error);
      }
    }

    return config;
  },
  error => Promise.reject(error)
);

instance.interceptors.response.use(
  response => response,
  async error => {
    if (error.response?.status === 503 && isCpanelPath()) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && isCpanelPath()) {
      if (window.location.pathname.startsWith('/cpanel')) {
        logoutCpanel({ hardRedirect: true });
      } else {
        clearCpanelToken();
        clearCentralUser();
      }
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !isCpanelPath()) {
      clearHospitalToken();
      if (!isHospitalLoginRoute()) {
        setHrefWithSlugHook('/login');
      }
      return Promise.reject(error);
    }

    if (error.response && error.response.status === 403) {
      const errorMessage = error.response?.data?.message || 'Anda tidak memiliki akses untuk mengakses halaman/fitur ini.';
      
      MySwal.fire({
        icon: 'error',
        title: 'Akses Ditolak!',
        text: errorMessage,
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: {
          popup: 'swal2-noanimation'
        }
      }).then(async () => {
        try {
          if (isCpanelPath()) {
            logoutCpanel({ hardRedirect: true });
            return;
          }

          const token = localStorage.getItem(`token${getSlug() ? '-' + getSlug() : ''}`);
          if (token) {
            await axios({
              url: '/users/logout',
              method: 'POST',
              headers: {
                'Access-Control-Allow-Origin': '*',
                Authorization: `Bearer ${token}`,
              },
            }).catch(() => {});
          }
        } catch (err) {
          // Ignore logout errors
        } finally {
          if (!isCpanelPath()) {
            clearHospitalToken();
            setHrefWithSlugHook('/login');
          }
        }
      });
    }
    
    return Promise.reject(error);
  }
);

export default instance;
